import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Card, Col, Container, Form, Row, Tab, Tabs } from 'react-bootstrap';
import Common from '../../shared/Common';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import './style.css';
import { DictionaryContext } from '../../../App';

interface IGenericFinanciaValue {
  year: number;
  week: number;
  month: number;
  totalExpense: number;
  totalIncome: number;
  balance: number;
}

const FinancialStatistics = () => {
  const [weeklyFinancialData, setWeeklyFinancialData] = useState<IGenericFinanciaValue[] | undefined>(undefined);
  const [monthlyFinancialData, setMonthlyFinancialData] = useState<IGenericFinanciaValue[] | undefined>(undefined);
  const [includeFuture, setIncludeFuture] = useState<boolean>(false);
  const [includeCurrentMonth, setIncludeCurrentMonth] = useState<boolean>(false);
  const [showLastYear, setShowLastYear] = useState<boolean>(true);
  const [groupByYears, setGroupByYears] = useState<boolean>(false);
  const [showMonthlyIncomes, setShowMonthlyIncomes] = useState<boolean>(false);
  const [showWeeklyIncomes, setShowWeeklyIncomes] = useState<boolean>(false);
  const [showMonthlyExpenses, setShowMonthlyExpenses] = useState<boolean>(true);
  const [showWeeklyExpenses, setShowWeeklyExpenses] = useState<boolean>(true);
  const [showMonthlyBalance, setShowMonthlyBalance] = useState<boolean>(false);
  const [showWeeklyBalance, setShowWeeklyBalance] = useState<boolean>(false);
  const [showReferenceLines, setShowReferenceLines] = useState<boolean>(false);
  const [showMonthlyChart, setShowMonthlyChart] = useState<boolean>(true);
  const [showWeeklyChart, setShowWeeklyChart] = useState<boolean>(false);
  const [balanceTarget, setBalanceTarget] = useState(0);
  const [chartHeight, setChartHeight] = useState(400);

  const fetchWeeklyFinancialDataIdRef = useRef(0);
  const fetchMonthlyFinancialDataRef = useRef(0);
  
  const { userPreferences } = useContext(DictionaryContext);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1000 && window.innerWidth > window.innerHeight) {
        var mobileHeight = window.innerHeight - 100;
        if (mobileHeight <= 250) {
          mobileHeight = 250;
        }
        setChartHeight(mobileHeight);
      } else {
        setChartHeight(400);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); 

  useEffect(() => {
    if (userPreferences?.monthlyTargetBalance) {
      setBalanceTarget(userPreferences?.monthlyTargetBalance);
    }
  }, [userPreferences])

  const fetchWeeklyFinancialData = useCallback(async () => {
    const fetchId = ++fetchWeeklyFinancialDataIdRef.current;

    const response = await Common.authorizedFetch('api/statistics/GetWeeklyFinancialSummary');
    if (fetchId === fetchWeeklyFinancialDataIdRef.current) {
      const data = await response.json();
      if (data.success) {
        setWeeklyFinancialData(data.result.data);
      }
    }
  }, []);

  const fetchMonthlyFinancialData = useCallback(async () => {
    const fetchId = ++fetchMonthlyFinancialDataRef.current;

    const response = await Common.authorizedFetch('api/statistics/GetMonthlyFinancialSummary');
    if (fetchId === fetchMonthlyFinancialDataRef.current) {
      const data = await response.json();
      if (data.success) {
        setMonthlyFinancialData(data.result.data);
      }
    }
  }, []);

  useEffect(() => {
    fetchWeeklyFinancialData();
    fetchMonthlyFinancialData();
  }, [fetchMonthlyFinancialData, fetchWeeklyFinancialData]);

  const generateFinancialData = useCallback(
    (financialData: IGenericFinanciaValue[] | undefined, type: string) => {
      const isWeekly = type === 'weekly';
      const showChart = isWeekly ? showWeeklyChart : showMonthlyChart;
      const setShowChart = isWeekly ? setShowWeeklyChart : setShowMonthlyChart;
      const showExpenses = isWeekly ? showWeeklyExpenses : showMonthlyExpenses;
      const showIncomes = isWeekly ? showWeeklyIncomes : showMonthlyIncomes;
      const showBalance = isWeekly ? showWeeklyBalance : showMonthlyBalance;
      const setShowExpenses = isWeekly ? setShowWeeklyExpenses : setShowMonthlyExpenses;
      const setShowIncomes = isWeekly ? setShowWeeklyIncomes : setShowMonthlyIncomes;
      const setShowBalance = isWeekly ? setShowWeeklyBalance : setShowMonthlyBalance;

      const generateChartData = (data: IGenericFinanciaValue[]) => {
        var chartData = data;
        const currentDate = new Date();
        if (!includeFuture) {
          var date = currentDate;
          if (isWeekly) {
            if (!includeCurrentMonth) {
              date.setDate(1);
              date.setDate(date.getDate() - 1);
            }
            chartData = chartData.filter((e) => currentDate >= Common.Utils.getDateOfIsoWeek(e.week, e.year));
          } else {
            if (!includeCurrentMonth) {
              date.setDate(1);
              date.setDate(date.getDate() - 1);
            }
            chartData = chartData.filter((e) => currentDate >= new Date(e.year, e.month - 1));
          }
        }
        if (showLastYear) {
          const currentDate = new Date();
          if (isWeekly) {
            const oneYearAgo = new Date(
              currentDate.getFullYear(),
              currentDate.getMonth(),
              currentDate.getDate() - 7 * 54
            );
            chartData = chartData.filter(
              (e) =>
                new Date() >= Common.Utils.getDateOfIsoWeek(e.week, e.year) &&
                Common.Utils.getDateOfIsoWeek(e.week, e.year) >= oneYearAgo
            );
          } else {
            const oneYearAgo = new Date(currentDate.getFullYear(), currentDate.getMonth() - 13, currentDate.getDate());
            chartData = chartData.filter((e) => new Date(e.year, e.month - 1) >= oneYearAgo);
          }
        }

        chartData = chartData.map((item) => ({
          ...item,
          balance: Number((item.totalIncome - item.totalExpense).toFixed(2)),
        }));

        const avgExpense =
          chartData.length > 0 ? chartData.reduce((acc, val) => acc + val.totalExpense, 0) / chartData.length : 0;
        const avgIncome =
          chartData.length > 0 ? chartData.reduce((acc, val) => acc + val.totalIncome, 0) / chartData.length : 0;
        const avgBalance =
          chartData.length > 0 ? chartData.reduce((acc, val) => acc + val.balance, 0) / chartData.length : 0;

        const expensesColor = '#DC143C';
        const incomeColor = '#2E8B57';
        const balanceColor = 'rgb(0, 0, 205)';

        const RenderLegend = () => (
          <Container>
            <Row xs='auto' className='justify-content-center align-items-center'>
              <Col>
                <Form.Check
                  id={`show-expenses-${type}-switch`}
                  label={<div style={{ color: expensesColor }}>Wydatki</div>}
                  style={{ userSelect: 'none' }}
                  checked={showExpenses}
                  onChange={() => {
                    setShowExpenses((prev) => !prev);
                  }}
                />
              </Col>
              <Col>
                <Form.Check
                  id={`show-incomes-${type}-switch`}
                  label={<div style={{ color: incomeColor }}>Przychody</div>}
                  style={{ userSelect: 'none' }}
                  checked={showIncomes}
                  onChange={() => {
                    setShowIncomes((prev) => !prev);
                  }}
                />
              </Col>
              <Col>
                <Form.Check
                  id={`show-balance-${type}-switch`}
                  label={<div style={{ color: balanceColor }}>Bilans</div>}
                  style={{ userSelect: 'none' }}
                  checked={showBalance}
                  onChange={() => {
                    setShowBalance((prev) => !prev);
                  }}
                />
              </Col>
            </Row>
          </Container>
        );

        const gradientOffset = () => {
          const threshold = isWeekly ? 0 : balanceTarget;
          const dataMax = Math.max(...chartData.map((i) => i.balance)) - threshold;
          const dataMin = Math.min(...chartData.map((i) => i.balance)) - threshold;

          if (dataMax <= 0) {
            return 0;
          }
          if (dataMin >= 0) {
            return 1;
          }

          return dataMax / (dataMax - dataMin);
        };

        const off = gradientOffset();


        return (
          <div className='overflow-auto'>
            <ResponsiveContainer minWidth={600} width='100%' height={chartHeight}>
              <ComposedChart
                data={chartData}
                margin={{
                  left: 10,
                  right: 25,
                  top: 5,
                  bottom: 5,
                }}
              >
                <Scatter key={0} name={'Wydatki'} line={{ strokeWidth: 0.75 }} fill='#0d6efd' cursor='pointer' />
                <CartesianGrid strokeDasharray='1' />
                <XAxis dataKey={isWeekly ? 'week' : 'month'} name={isWeekly ? 'Tydzień' : 'Miesiąc'} />
                <YAxis unit=' zł' />
                {showBalance && (
                  <>
                    <defs>
                      <linearGradient id='splitColorLowOpacity' x1='0' y1='0' x2='0' y2='1'>
                        <stop offset={off} stopColor={balanceColor} stopOpacity={0.1} />
                        <stop offset={off} stopColor='#6e2c00' stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id='splitColor' x1='0' y1='0' x2='0' y2='1'>
                        <stop offset={off} stopColor={balanceColor} stopOpacity={1} />
                        <stop offset={off} stopColor='#6e2c00' stopOpacity={1} />
                      </linearGradient>
                    </defs>
                    <Area
                      type='monotone'
                      dataKey='balance'
                      stroke={isWeekly ? balanceColor : 'url(#splitColor)'}
                      fill={isWeekly ? balanceColor : 'url(#splitColorLowOpacity)'}
                      name='Bilans'
                      unit=' zł'
                      activeDot={{ r: 6 }}
                      baseValue={isWeekly ? 0 : balanceTarget}
                    />
                    <ReferenceLine
                      y={isWeekly ? 0 : balanceTarget}
                      strokeWidth={0.7}
                      stroke={balanceColor}
                      label={
                        !isWeekly && {
                          position: 'right',
                          value: 'cel',
                          style: { fontSize: '14px', fill: balanceColor },
                        }
                      }
                    />
                  </>
                )}
                {showExpenses && (
                  <Line
                    type='monotone'
                    dataKey='totalExpense'
                    stroke={expensesColor}
                    name='Wydatki'
                    unit=' zł'
                    activeDot={{ r: 6 }}
                  />
                )}
                {showIncomes && (
                  <Line
                    type='monotone'
                    dataKey='totalIncome'
                    stroke={incomeColor}
                    name='Przychody'
                    unit=' zł'
                    activeDot={{ r: 6 }}
                  />
                )}
                <Legend content={<RenderLegend />} verticalAlign='top' iconSize={10} height={36} />
                <Legend verticalAlign='top' iconSize={10} height={36} />
                {showReferenceLines && (
                  <>
                    {showExpenses && (
                      <ReferenceLine
                        y={avgExpense}
                        label={{
                          dx: 10,
                          dy: -10,
                          value: `średnia: ${avgExpense.toFixed(2)} zł`,
                          style: { fontSize: '14px', fontWeight: 'bold', fill: expensesColor },
                        }}
                        stroke={expensesColor}
                        strokeDasharray='3 3'
                      />
                    )}
                    {showIncomes && (
                      <ReferenceLine
                        y={avgIncome}
                        label={{
                          dx: -200,
                          dy: -10,
                          value: `średnia: ${avgIncome.toFixed(2)} zł`,
                          style: { fontSize: '14px', fontWeight: 'bold', fill: incomeColor },
                        }}
                        stroke={incomeColor}
                        strokeDasharray='3 3'
                      />
                    )}
                    {showBalance && (
                      <ReferenceLine
                        y={avgBalance}
                        label={{
                          dx: 200,
                          dy: -10,
                          value: `średnia: ${avgBalance.toFixed(2)} zł`,
                          style: { fontSize: '14px', fontWeight: 'bold', fill: balanceColor },
                        }}
                        stroke={balanceColor}
                        strokeDasharray='3 3'
                      />
                    )}
                  </>
                )}
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const totalExpense = payload.find((p) => p.dataKey === 'totalExpense');
                      const totalIncome = payload.find((p) => p.dataKey === 'totalIncome');
                      const balance = payload.find((p) => p.dataKey === 'balance');
                      var tooltipLabel: JSX.Element;
                      if (isWeekly) {
                        if (groupByYears) {
                          tooltipLabel = <Row className='tooltip-intro'>{`Tydzień ${label}`}</Row>;
                        } else {
                          const year = payload[0].payload.year;
                          tooltipLabel = <Row className='tooltip-intro'>{`Tydzień ${label}, ${year}`}</Row>;
                        }
                      } else {
                        if (label.toString().length <= 1) {
                          label = '0' + label;
                        }
                        const year = payload[0].payload.year;
                        tooltipLabel = <Row className='tooltip-intro'>{`${label}.${year}`}</Row>;
                      }

                      return (
                        <Container className='custom-tooltip'>
                          {tooltipLabel}
                          {totalExpense && (
                            <Row className='tooltip-expenses'>{`Wydatki: ${Number(totalExpense.value).toFixed(2)} zł`}</Row>
                          )}
                          {totalIncome && (
                            <Row className='tooltip-incomes'>{`Przychody: ${Number(totalIncome.value).toFixed(2)} zł`}</Row>
                          )}
                          {balance && (
                            <Row className='tooltip-balance'>{`Bilans: ${Number(balance.value).toFixed(2)} zł`}</Row>
                          )}
                        </Container>
                      );
                    }

                    return null;
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        );
      };
      const generateTableData = (data: IGenericFinanciaValue[]) => {
        var chartData = data;
        if (!includeFuture) {
          if (isWeekly) {
            chartData = chartData.filter((e) => new Date() >= Common.Utils.getDateOfIsoWeek(e.week, e.year));
          } else {
            chartData = chartData.filter((e) => new Date() >= new Date(e.year, e.month - 1));
          }
        }
        const items = [];
        for (var itemInd = chartData.length - 1; itemInd >= 0; itemInd--) {
          const curentRow = chartData[itemInd];
          const previousRow = itemInd - 1 >= 0 ? chartData[itemInd - 1] : chartData[itemInd];
          const absoluteDiffrence = curentRow.totalIncome - curentRow.totalExpense;
          const previousRowAbsoluteDiffrence = previousRow.totalIncome - previousRow.totalExpense;
          let relativeDiffrence;
          if (previousRowAbsoluteDiffrence < 0 && absoluteDiffrence > 0) {
            relativeDiffrence = 100; // Traktujemy przejście z ujemnej do dodatniej wartości jako 100% wzrost
          } else {
            relativeDiffrence =
              ((absoluteDiffrence - previousRowAbsoluteDiffrence) / Math.abs(previousRowAbsoluteDiffrence)) * 100;
          }

          items.push(
            <tr key={chartData + '-' + itemInd}>
              <td>{isWeekly ? chartData[itemInd].week : chartData[itemInd].month}</td>
              <td>{Common.Utils.getCurrencyString(curentRow.totalIncome.toFixed(2))}</td>
              <td>{Common.Utils.getCurrencyString(curentRow.totalExpense.toFixed(2))}</td>
              <td>{Common.Utils.getCurrencyString(absoluteDiffrence.toFixed(2))}</td>
              <td
                style={{
                  color:
                    isNaN(relativeDiffrence) || !isFinite(relativeDiffrence)
                      ? 'black'
                      : relativeDiffrence < 0
                        ? 'red'
                        : '#00aa5b',
                  fontWeight: 'bold',
                }}
              >
                {isNaN(relativeDiffrence) || !isFinite(relativeDiffrence) ? '---' : relativeDiffrence.toFixed(2) + '%'}
              </td>
            </tr>
          );
        }
        return (
          <div className='table-responsive' style={{ maxHeight: '400px', overflow: 'auto' }}>
            <table className='table table-without-border-bottom'>
              <thead>
                <tr>
                  <th scope='col'>{isWeekly ? 'Tydzień' : 'Miesiąc'}</th>
                  <th scope='col'>Suma przychodów</th>
                  <th scope='col'>Suma wydatków</th>
                  <th scope='col'>Bilans</th>
                  <th scope='col'>Różnica względna</th>
                </tr>
              </thead>
              <tbody>{items}</tbody>
            </table>
          </div>
        );
      };
      var cardBody: JSX.Element;
      if (financialData === undefined) {
        cardBody = <div style={{ height: 200 }}>{Common.Ui.showLoadingSpinnerAbsolute(true)}</div>;
      } else {
        if (financialData.length === 0) {
          return <></>;
        } else {
          if (!groupByYears) {
            if (showChart) {
              cardBody = generateChartData(financialData);
            } else {
              cardBody = generateTableData(financialData);
            }
          } else {
            const tabs: JSX.Element[] = [];
            var yearGroup = Common.Utils.groupBy(financialData, (i) => i.year);
            const yearsDescending = Object.keys(yearGroup)
              .map((e) => Number(e))
              .sort()
              .reverse();
            for (var ind = 0; ind < yearsDescending.length; ind++) {
              const year = yearsDescending[ind];
              const yearData = yearGroup[year];
              if (showChart) {
                tabs.push(
                  <Tab key={ind + '-' + year} eventKey={ind + '-' + year} title={year}>
                    {generateChartData(yearData)}
                  </Tab>
                );
              } else {
                if (yearData.length > 0) {
                  tabs.push(
                    <Tab key={ind + '-' + year} eventKey={ind + '-' + year} title={year}>
                      {generateTableData(yearData)}
                    </Tab>
                  );
                }
              }
            }
            cardBody = (
              <Tabs key='' className='mb-3'>
                {tabs}
              </Tabs>
            );
          }
        }
      }

      return (
        <Card className='mt-3 mb-2'>
          <Card.Header className='statistics-card-header'>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>{isWeekly ? 'Statystyki tygodniowe' : 'Statystyki miesięczne'}</div>
              <div>
                <Form.Check
                  type='switch'
                  id={`show-chart-${type}-switch`}
                  label='Prezentuj wykres'
                  style={{ userSelect: 'none' }}
                  checked={showChart}
                  onChange={() => {
                    setShowChart((prev) => !prev);
                  }}
                />
              </div>
            </div>
          </Card.Header>
          <Card.Body className='pb-0 pt-2'>{cardBody}</Card.Body>
        </Card>
      );
    },
    [
      includeCurrentMonth,
      includeFuture,
      groupByYears,
      showMonthlyChart,
      showWeeklyChart,
      showWeeklyBalance,
      showMonthlyBalance,
      showWeeklyExpenses,
      showMonthlyExpenses,
      showWeeklyIncomes,
      showMonthlyIncomes,
      showReferenceLines,
      chartHeight,
      showLastYear,
      balanceTarget
    ]
  );

  const switchesDisabled = !showMonthlyChart && !showWeeklyChart;

  return (
    <>
      {weeklyFinancialData?.length !== 0 && monthlyFinancialData?.length !== 0 && (
        <div
          className='mt-3 px-3 py-3'
          style={{
            borderRadius: '5px',
            backgroundColor: '#00aa5b',
            marginBottom: '15px',
            color: 'white',
          }}
        >
          <Row className='mt-2'>
            <Col xs={6} md={4}>
              <Form.Check
                disabled={switchesDisabled}
                type='switch'
                id='include-future-switch'
                style={{ userSelect: 'none' }}
                label='Uwzględnij przyszłe'
                checked={includeFuture}
                onChange={() => {
                  if (includeCurrentMonth && !includeFuture) {
                    setIncludeCurrentMonth(false);
                  }
                  setIncludeFuture((prev) => !prev);
                }}
              />
            </Col>
            <Col xs={6} md={4}>
              <Form.Check
                disabled={switchesDisabled}
                type='switch'
                id='include-current-month-switch'
                style={{ userSelect: 'none' }}
                label='Uwzględnij bieżący miesiąc'
                checked={includeCurrentMonth}
                onChange={() => {
                  if (!includeCurrentMonth && includeFuture) {
                    setIncludeFuture(false);
                  }
                  setIncludeCurrentMonth((prev) => !prev);
                }}
              />
            </Col>
            <Col xs={6} md={4}>
              <Form.Check
                disabled={switchesDisabled}
                type='switch'
                id='show-last-12-months-switch'
                style={{ userSelect: 'none' }}
                label='Pokaż ostatni rok'
                checked={showLastYear}
                onChange={() => {
                  if (!showLastYear && groupByYears) {
                    setGroupByYears(false);
                  }
                  setShowLastYear((prev) => !prev);
                }}
              />
            </Col>
            <Col xs={6} md={4}>
              <Form.Check
                disabled={switchesDisabled}
                type='switch'
                id='group-by-year-switch'
                style={{ userSelect: 'none' }}
                label='Grupuj po latach'
                checked={groupByYears}
                onChange={() => {
                  if (showLastYear && !groupByYears) {
                    setShowLastYear(false);
                  }
                  setGroupByYears((prev) => !prev);
                }}
              />
            </Col>
            <Col xs={6} md={4}>
              <Form.Check
                disabled={switchesDisabled}
                type='switch'
                id='show-reference-lines-switch'
                style={{ userSelect: 'none' }}
                label='Wyświetl średnie'
                checked={showReferenceLines}
                onChange={() => {
                  setShowReferenceLines((prev) => !prev);
                }}
              />
            </Col>
          </Row>
        </div>
      )}
      {generateFinancialData(monthlyFinancialData, 'monthly')}
      {generateFinancialData(weeklyFinancialData, 'weekly')}
    </>
  );
};

export default FinancialStatistics;
